import type { ContractDetails, ContractListResponse, ContractSummary } from "@/domain/contract";
import { ContractDisplayStatus } from "@/domain/documentEnums";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { detailSlot, isObject } from "../cache/cacheSlots";
import { aggregateSlot, flatAggregates } from "../cache/aggregateSlots";
import { defineEntityCache } from "../cache/entityCache";
import {
  anyOf,
  byUuid,
  distinctPages,
  viewSlot,
  type ViewRules,
  type ViewShape,
} from "../cache/listViews";

export type ContractPatch = Partial<ContractSummary>;

const page: ViewShape<ContractListResponse, ContractSummary, string> = {
  isPage: (value): value is ContractListResponse =>
    isObject(value) && Array.isArray(value.contracts) && typeof value.total_count === "number",
  records: (current) => current.contracts,
  withRecords: (current, contracts, countDelta) => ({
    ...current,
    contracts,
    total_count: current.total_count + countDelta,
  }),
  idOf: (record) => record.document_uuid,
  total: (current) => current.total_count,
};

export const distinctContractPages = distinctPages(page);

function isContractDetails(value: unknown): value is ContractDetails {
  return isObject(value) && typeof value.document_uuid === "string" && "signature_status" in value;
}

/** The dates the detail shares with the listed contract. */
const mergeDetails = (record: ContractDetails, patch: ContractPatch): ContractDetails => ({
  ...record,
  ...(patch.sent_at !== undefined ? { sent_at: patch.sent_at } : {}),
  ...(patch.completed_at !== undefined ? { completed_at: patch.completed_at } : {}),
  ...(patch.voided_at !== undefined ? { voided_at: patch.voided_at } : {}),
});

const merge = (record: ContractSummary, patch: ContractPatch): ContractSummary => ({
  ...record,
  ...patch,
});

const time = (value: string | null | undefined) => (value ? Date.parse(value) : null);

export const contractRules: ViewRules<ContractSummary> = {
  accepts: { status: anyOf((contract) => contract.status) },
  narrowing: ["search", "contract_type", "beneficiary_uuid", "investor_uuid", "document_uuid"],
  order: {
    sent_at: (contract) => time(contract.sent_at),
    created_at: (contract) => time(contract.created_at),
    investment_amount: (contract) =>
      contract.investment_amount == null ? null : Number(contract.investment_amount),
  },
  tieBreak: byUuid("desc", (contract) => contract.document_uuid),
};

const COMPLETED: readonly string[] = [ContractDisplayStatus.SIGNED, ContractDisplayStatus.APPROVED];

const aggregates = flatAggregates<ContractSummary>({
  sums: { "investment_amount:sum": (contract) => contract.investment_amount },
  counts: {
    ...Object.fromEntries(
      Object.values(ContractDisplayStatus).map((status) => [
        `status:${status}`,
        (contract: ContractSummary) => contract.status === status,
      ]),
    ),
    "status:completed": (contract) => COMPLETED.includes(contract.status),
  },
  distributions: { "contract_type:distribution": (contract) => [contract.contract_type] },
  ratios: { "status:pct_completed": (contract) => COMPLETED.includes(contract.status) },
});

export const contractCache = defineEntityCache<string, ContractPatch, ContractSummary>({
  entity: InvalidationEntity.CONTRACT,
  row: merge,
  slots: (orgId) => [
    viewSlot({
      prefix: queryKeys.contracts.lists(orgId),
      shape: page,
      merge,
      parse: queryKeys.contracts.parseList,
      rules: contractRules,
    }),
    aggregateSlot({
      prefix: queryKeys.contracts.aggregatesAll(orgId),
      rules: contractRules,
      parse: queryKeys.contracts.parseAggregates,
      ...aggregates,
      sizeFrom: {
        prefix: queryKeys.contracts.lists(orgId),
        parse: queryKeys.contracts.parseList,
        total: (current) => (page.isPage(current) ? current.total_count : undefined),
      },
    }),
    detailSlot({
      prefix: queryKeys.contracts.details(orgId),
      isRecord: isContractDetails,
      idOf: (record) => record.document_uuid,
      merge: mergeDetails,
    }),
  ],
  records: (orgId, documentUuid) => [queryKeys.contracts.detail(orgId, documentUuid)],
});
